import seedrandom from "seedrandom";

// Path to the curated word list
const CURATED_WORDS_PATH = "/assets/words/curated_5.txt";

// Will hold the Set of valid 5-letter words
let VALID_5_LETTER_WORDS = null;

/**
 * Loads the curated 5-letter word list from the public assets folder.
 * Returns a Set of uppercase words.
 */
export async function loadValidWords() {
  if (VALID_5_LETTER_WORDS) return VALID_5_LETTER_WORDS;
  const response = await fetch(CURATED_WORDS_PATH);
  const text = await response.text();
  VALID_5_LETTER_WORDS = new Set(
    text
      .split("\n")
      .map((w) => w.trim().toUpperCase())
      .filter((w) => w.length === 5)
  );
  return VALID_5_LETTER_WORDS;
}

// Common 6-9 letter words for source selection
const SOURCE_WORDS = [
  // 6 letters
  "ACTION",
  "BEAUTY",
  "CHANGE",
  "DREAMS",
  "ENERGY",
  "FAMILY",
  "GARDEN",
  "HEALTH",
  "INSIDE",
  "JOURNEY",
  "KNIGHT",
  "LITTLE",
  "MUSIC",
  "NATURE",
  "OFFICE",
  "PERSON",
  "QUIET",
  "RADIO",
  "SIMPLE",
  "TRAVEL",
  "UNITED",
  "VOICE",
  "WINTER",
  "YOUTH",

  // 7 letters
  "ANIMALS",
  "BEAUTIFUL",
  "CLEAN",
  "DANGER",
  "EVERY",
  "FRIEND",
  "GENERAL",
  "HAPPY",
  "IMPORTANT",
  "KNOWLEDGE",
  "LANGUAGE",
  "MORNING",
  "NATURAL",
  "OPEN",
  "PICTURE",
  "REALITY",
  "SERVICE",
  "THOUGHT",
  "UNIVERSAL",
  "VICTORY",
  "WONDERFUL",
  "YEARLY",

  // 8 letters
  "ABSOLUTE",
  "COMPLETE",
  "DISTANCE",
  "EVERYTHING",
  "HAPPINESS",
  "IMPORTANT",
  "KNOWLEDGE",
  "LANGUAGE",
  "MORNING",
  "NATURAL",
  "PICTURE",
  "REALITY",
  "SERVICE",
  "THOUGHT",
  "UNIVERSAL",
  "VICTORY",
  "WONDERFUL",
  "YEARLY",

  // 9 letters
  "ABSOLUTE",
  "BEAUTIFUL",
  "COMPLETE",
  "DISTANCE",
  "EVERYTHING",
  "HAPPINESS",
  "IMPORTANT",
  "KNOWLEDGE",
  "LANGUAGE",
  "MORNING",
  "NATURAL",
  "PICTURE",
  "REALITY",
  "SERVICE",
  "THOUGHT",
  "UNIVERSAL",
  "VICTORY",
  "WONDERFUL",
  "YEARLY",
];

/**
 * Generate a date-based seed string
 * @param {Date} date - The date to generate a seed for
 * @returns {string} - Seed string in YYYY-MM-DD format
 */
export const getDateSeed = (date) => {
  return date.toISOString().slice(0, 10);
};

/**
 * Generate 5 random source words (6-9 letters) based on a date seed
 * @param {Date} date - The date to generate words for
 * @returns {string[]} - Array of 5 source words
 */
export const generateSourceWords = (date) => {
  const seed = getDateSeed(date);
  const rng = seedrandom(seed);

  // Filter words by length (6-9 letters)
  const eligibleWords = SOURCE_WORDS.filter(
    (word) => word.length >= 6 && word.length <= 9
  );

  // Shuffle and pick 5 unique words
  const shuffled = [...eligibleWords].sort(() => rng() - 0.5);
  const selected = [];

  for (let i = 0; i < shuffled.length && selected.length < 5; i++) {
    const word = shuffled[i];
    if (!selected.includes(word)) {
      selected.push(word);
    }
  }

  // If we don't have enough words, pad with some defaults
  while (selected.length < 5) {
    selected.push("ACTION");
  }

  return selected.slice(0, 5);
};

/**
 * Generate all possible 5-letter word permutations from source words, using the loaded word set.
 * @param {string[]} sourceWords - Array of source words
 * @returns {Promise<string[]>} - Array of valid 5-letter words that can be formed
 */
export async function generatePossibleWords(sourceWords) {
  const validWords = await loadValidWords();
  const possibleWords = new Set();

  // Generate all permutations (1 letter from each word)
  for (let i = 0; i < sourceWords[0].length; i++) {
    for (let j = 0; j < sourceWords[1].length; j++) {
      for (let k = 0; k < sourceWords[2].length; k++) {
        for (let l = 0; l < sourceWords[3].length; l++) {
          for (let m = 0; m < sourceWords[4].length; m++) {
            const word = (
              sourceWords[0][i] +
              sourceWords[1][j] +
              sourceWords[2][k] +
              sourceWords[3][l] +
              sourceWords[4][m]
            ).toUpperCase();

            // Check if it's a valid word
            if (validWords.has(word)) {
              possibleWords.add(word);
            }
          }
        }
      }
    }
  }

  return Array.from(possibleWords).sort();
}

/**
 * Get the complete word set for a given date (async)
 * @param {Date} date - The date to generate words for
 * @returns {Promise<Object>} - Object containing sourceWords and possibleWords
 */
export async function getWordsForDate(date) {
  const sourceWords = generateSourceWords(date);
  const possibleWords = await generatePossibleWords(sourceWords);

  return {
    sourceWords,
    possibleWords,
    totalPossible: possibleWords.length,
  };
}

/**
 * Cache for storing generated word sets
 */
const wordCache = new Map();

/**
 * Get cached words for a date, or generate new ones (async)
 * @param {Date} date - The date to get words for
 * @returns {Promise<Object>} - Object containing sourceWords and possibleWords
 */
export async function getCachedWordsForDate(date) {
  const dateKey = getDateSeed(date);

  if (wordCache.has(dateKey)) {
    return wordCache.get(dateKey);
  }

  const words = await getWordsForDate(date);
  wordCache.set(dateKey, words);

  return words;
}
